import React, { createContext, useContext, useRef } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { useSurface, SurfaceProvider } from "../../lib/surface-context";
import { surfaceClasses } from "../../lib/surface-classes";
import { useFluidHover, useRegisterFluidHoverItem } from "../../lib/use-fluid-hover";
import { FluidHoverHighlight } from "../FluidHoverHighlight";
import { ScrollArea } from "../ScrollArea";

/* ============================================================================
   The popup shared by the table's "View" and "Filters" menus.

     <MenuPanel title="Filters" align="start" footer={clearButton}>
       {values.map((v, i) => <MenuCheck key={v} index={i} ... />)}
     </MenuPanel>

   - Elevation: two surface steps above where it opens, shadow pinned to 3,
     so it reads the same on a page or inside a dialog.
   - Hover: one fluid-hover fill glides between the rows instead of each row
     lighting its own background. Rows join it through MenuCheck's `index`.
   - Long lists scroll inside a ScrollArea (max 320px) with a scroll fade, so
     the title and the footer stay put.
   ========================================================================== */

const MenuHoverContext = createContext(undefined);

export const MenuPanel = ({ title, align, footer, children }) => {
  const level = Math.min(useSurface() + 2, 8);
  const listRef = useRef(null);
  const hover = useFluidHover(listRef);

  return (
    <Dropdown.Menu className={"dt-menu-panel " + surfaceClasses(level, 3)} align={align}>
      <SurfaceProvider value={level}>
        {title ? <div className="dt-menu-title">{title}</div> : null}
        <ScrollArea viewportClassName="dt-menu-viewport scroll-fade">
          <div ref={listRef} className="dt-menu-list" {...hover.handlers}>
            <FluidHoverHighlight hover={hover} className="dt-menu-hover" />
            <MenuHoverContext.Provider value={hover.registerItem}>
              {children}
            </MenuHoverContext.Provider>
          </div>
        </ScrollArea>
        {footer}
      </SurfaceProvider>
    </Dropdown.Menu>
  );
};

/** One checkbox row of a MenuPanel. `index` is its place in the list (0-based). */
export const MenuCheck = ({ index, checked, onChange, label, count }) => {
  const ref = useRef(null);
  useRegisterFluidHoverItem(useContext(MenuHoverContext), index, ref);

  return (
    <label className="dt-check" ref={ref}>
      <input
        type="checkbox"
        className="form-check-input"
        checked={checked}
        onChange={onChange}
      />
      <span className="dt-check-label">{label}</span>
      {count !== undefined ? <span className="dt-check-count">{count}</span> : null}
    </label>
  );
};
